import { useCallback, useEffect, useState } from "react";
import { LuPencil, LuTrash2, LuClipboard } from "react-icons/lu";
import { api, ApiError, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import { APPLIANCE_LABEL } from "../lib/types";
import type { Appliance, ApplianceName, Flat, User } from "../lib/types";
import { Avatar } from "../components/Avatar";
import {
  Button,
  Card,
  CenteredSpinner,
  ErrorBanner,
  IconButton,
  Input,
  Label,
  Pill,
  Select,
  SectionHeader,
} from "../components/ui";
import { formatDate } from "../lib/format";
import { env } from "../lib/env";
import {
  getPushStatus,
  enablePush,
  disablePush,
  pushSupported,
  type PushStatus,
} from "../lib/push";
import { useCurrency, CURRENCY_LABEL, type Currency } from "../lib/currency";

const APPLIANCE_OPTIONS: ApplianceName[] = ["WASHER", "DRYER", "DISHWASHER"];

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { flat, reload } = useActiveFlat();
  const myRole = flat!.members.find((m) => m.userId === user!.id)?.role ?? "MEMBER";
  const isAdmin = myRole === "ADMIN";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Manage your profile and your flat.</p>
      </div>

      <ProfileSection user={user!} onSaved={refreshUser} />
      <CurrencySection />
      <NotificationsSection flatId={flat!.id} userId={user!.id} />
      <FlatSection flat={flat!} isAdmin={isAdmin} onChanged={reload} />
      {isAdmin && <ApplianceSection flatId={flat!.id} />}
    </div>
  );
}

function CurrencySection() {
  const { currency, setCurrency } = useCurrency();
  return (
    <Card>
      <SectionHeader title="Currency" subtitle="Symbol shown on expenses — no conversion applied." />
      <div className="flex gap-2">
        {(["GBP", "USD"] as Currency[]).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCurrency(c)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              currency === c
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {CURRENCY_LABEL[c]}
          </button>
        ))}
      </div>
    </Card>
  );
}

function ProfileSection({
  user,
  onSaved,
}: {
  user: User;
  onSaved: () => Promise<User | null>;
}) {
  const [name, setName] = useState(user.name ?? "");
  const [colour, setColour] = useState(user.colour);
  const [venmoHandle, setVenmoHandle] = useState(user.venmoHandle ?? "");
  const [zelleHandle, setZelleHandle] = useState(user.zelleHandle ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      await api.patch<User>("/auth/me", {
        name: name.trim(),
        colour,
        venmoHandle: venmoHandle.trim() || null,
        zelleHandle: zelleHandle.trim() || null,
      });
      await onSaved();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(errMsg(err, "Couldn't save your profile."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <SectionHeader title="Profile" subtitle="How you appear in the flat." />
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
          <div className="flex flex-col items-center gap-2">
            <Avatar name={name || "?"} colour={colour} size="lg" />
            <input
              type="color"
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              className="h-8 w-16 cursor-pointer rounded border border-slate-200 bg-white"
              aria-label="Profile colour"
            />
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="pname">Display name</Label>
              <Input
                id="pname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="pcolour">Hex colour</Label>
              <Input
                id="pcolour"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                maxLength={7}
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-4">
          <div>
            <p className="text-sm font-medium text-slate-700">Payment handles</p>
            <p className="text-xs text-slate-500">
              Let flatmates pay you back in one tap. Visible to everyone in your flat. Venmo and
              Zelle are US-only — leave blank if you don't use them.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="pvenmo">Venmo username</Label>
              <Input
                id="pvenmo"
                value={venmoHandle}
                onChange={(e) => setVenmoHandle(e.target.value)}
                placeholder="your-venmo"
                disabled={saving}
              />
              <p className="mt-1 text-xs text-slate-400">
                Just your username — the leading @ is optional.
              </p>
            </div>
            <div>
              <Label htmlFor="pzelle">Zelle (email or US mobile)</Label>
              <Input
                id="pzelle"
                value={zelleHandle}
                onChange={(e) => setZelleHandle(e.target.value)}
                placeholder="you@example.com"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <ErrorBanner message={error} />
        {success && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Saved.
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" loading={saving}>
            Save changes
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt(`Copy ${label}:`, value);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={value} readOnly className="font-mono text-xs" />
        <button
          type="button"
          onClick={copy}
          title={copied ? "Copied!" : "Copy"}
          aria-label={copied ? "Copied!" : `Copy ${label}`}
          className={`shrink-0 rounded-lg border px-2.5 transition ${
            copied
              ? "border-emerald-200 bg-emerald-50 text-emerald-600"
              : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          }`}
        >
          <LuClipboard className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function NotificationsSection({ flatId, userId }: { flatId: string; userId: string }) {
  const topic = `flat-${flatId}-${userId}`;
  const serverUrl = env.VITE_NTFY_URL;

  return (
    <Card>
      <SectionHeader
        title="Notifications"
        subtitle="Push notifications for chores, expenses, laundry and the board."
      />

      <PushControls />

      <div className="mt-6 space-y-3 border-t border-slate-100 pt-6">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Or use ntfy instead
        </p>
        <p className="text-sm text-slate-600">
          Install{" "}
          <a
            href="https://ntfy.sh/"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-indigo-600 hover:underline"
          >
            ntfy
          </a>{" "}
          on your phone, open it, tap <span className="font-medium">+</span> and enter the server and topic below.
        </p>
        <CopyField label="Server URL" value={serverUrl} />
        <CopyField label="Topic" value={topic} />
      </div>
    </Card>
  );
}

function PushControls() {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPushStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  if (!pushSupported()) {
    return (
      <p className="text-sm text-slate-500">
        This browser doesn't support push notifications. On iPhone, add Flat to your home screen
        first, then open it from there.
      </p>
    );
  }

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await enablePush();
      setStatus(next);
      if (next.permission === "denied") {
        setError("Notifications are blocked. Enable them in your browser settings for this site.");
      }
    } catch (err) {
      setError(errMsg(err, "Couldn't enable notifications."));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      await disablePush();
      setStatus({ supported: true, permission: Notification.permission, subscribed: false });
    } catch (err) {
      setError(errMsg(err, "Couldn't turn off notifications."));
    } finally {
      setBusy(false);
    }
  };

  const subscribed = status?.subscribed ?? false;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full ${
              subscribed ? "bg-emerald-500" : "bg-slate-300"
            }`}
            aria-hidden
          />
          <span className="text-sm text-slate-700">
            {subscribed ? "This device is receiving notifications" : "This device is off"}
          </span>
        </div>
        {subscribed ? (
          <Button variant="secondary" onClick={disable} loading={busy}>
            Turn off
          </Button>
        ) : (
          <Button onClick={enable} loading={busy}>
            Turn on
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <p className="text-xs text-slate-400">
        Notifications are per-device — turn them on wherever you want alerts.
      </p>
    </div>
  );
}

function FlatSection({
  flat,
  isAdmin,
  onChanged,
}: {
  flat: Flat;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [flatName, setFlatName] = useState(flat.name);
  const [renameSaving, setRenameSaving] = useState(false);
  const rename = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = flatName.trim();
    if (!trimmed) return;
    setRenameSaving(true);
    setError(null);
    try {
      await api.patch(`/flat/${flat.id}`, { name: trimmed });
      await onChanged();
      setRenaming(false);
    } catch (err) {
      setError(errMsg(err, "Couldn't rename."));
    } finally {
      setRenameSaving(false);
    }
  };

  const cancelRename = () => {
    setFlatName(flat.name);
    setRenaming(false);
  };

  const inviteUrl = `${window.location.origin}/invite/${flat.inviteCode}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this invite link:", inviteUrl);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the invite code? The old link will stop working.")) return;
    setRegenerating(true);
    setError(null);
    try {
      await api.post<{ inviteCode: string }>(`/flat/${flat.id}/invite`);
      await onChanged();
    } catch (err) {
      setError(errMsg(err, "Couldn't regenerate."));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card>
      <div className="mb-4">
        {renaming ? (
          <form onSubmit={rename} className="flex items-center gap-2">
            <Input
              autoFocus
              value={flatName}
              onChange={(e) => setFlatName(e.target.value)}
              disabled={renameSaving}
              className="flex-1 text-lg font-semibold"
            />
            <Button type="submit" loading={renameSaving} disabled={!flatName.trim()}>Save</Button>
            <Button type="button" variant="secondary" onClick={cancelRename}>Cancel</Button>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">{flat.name}</h2>
            {isAdmin && (
              <IconButton
                icon={<LuPencil className="h-4 w-4" />}
                label="Rename flat"
                onClick={() => { setFlatName(flat.name); setRenaming(true); }}
              />
            )}
          </div>
        )}
        <p className="text-sm text-slate-500">Created {formatDate(flat.createdAt)}</p>
      </div>

      <div className="space-y-5">
        <div>
          <Label>Invite link</Label>
          <div className="flex gap-2">
            <Input value={inviteUrl} readOnly className="font-mono text-xs" />
            <button
              type="button"
              onClick={copy}
              title={copied ? "Copied!" : "Copy invite link"}
              aria-label={copied ? "Copied!" : "Copy invite link"}
              className={`shrink-0 rounded-lg border px-2.5 transition ${
                copied
                  ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              <LuClipboard className="h-4 w-4" />
            </button>
          </div>
          {isAdmin && (
            <button
              onClick={regenerate}
              disabled={regenerating}
              className="mt-2 text-xs font-medium text-slate-500 hover:text-rose-600 disabled:opacity-50"
            >
              {regenerating ? "Regenerating…" : "Regenerate invite code"}
            </button>
          )}
        </div>

        <ErrorBanner message={error} />

        <div>
          <Label>Members ({flat.members.length})</Label>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
            {flat.members.map((m) => (
              <li key={m.userId} className="flex items-center gap-3 px-3 py-2.5">
                <Avatar name={m.user.name} colour={m.user.colour} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-900">
                    {m.user.name ?? "—"}
                  </div>
                  <div className="truncate text-xs text-slate-500">{m.user.email}</div>
                </div>
                <Pill tone={m.role === "ADMIN" ? "brand" : "slate"}>
                  {m.role === "ADMIN" ? "Admin" : "Member"}
                </Pill>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

function ApplianceRow({
  appliance,
  appliances,
  onSaved,
}: {
  appliance: Appliance;
  appliances: Appliance[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [nextApplianceId, setNextApplianceId] = useState(appliance.nextApplianceId ?? "");
  const [haManaged, setHaManaged] = useState(appliance.haManaged);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [haCopied, setHaCopied] = useState(false);

  const nfcUrl = `${window.location.origin}/nfc?device=${appliance.id}`;

  const copyNfcUrl = () => {
    navigator.clipboard.writeText(nfcUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const webhookUrl = `${env.VITE_API_URL}/webhook/ha`;
  const haKey = appliance.name.toLowerCase();
  // Ready-to-paste HA config with the appliance ID injected — no hand-copying
  // the DB id. Power thresholds (5 W / 2 min) come from the measured wash-cycle
  // profile. The secret lives in HA secrets.yaml, never in this bundle.
  const haConfig = `# --- configuration.yaml ---
rest_command:
  flat_${haKey}_running:
    url: "${webhookUrl}"
    method: POST
    headers:
      x-webhook-secret: !secret flat_webhook_secret
      content-type: application/json
    payload: '{"applianceId":"${appliance.id}","state":"RUNNING"}'
  flat_${haKey}_done:
    url: "${webhookUrl}"
    method: POST
    headers:
      x-webhook-secret: !secret flat_webhook_secret
      content-type: application/json
    payload: '{"applianceId":"${appliance.id}","state":"DONE"}'

# --- automations.yaml (set <POWER_SENSOR> to your plug's power entity) ---
- alias: "Flat ${APPLIANCE_LABEL[appliance.name]} → running"
  trigger:
    - platform: numeric_state
      entity_id: <POWER_SENSOR>
      above: 5
      for: "00:00:30"
  action:
    - service: rest_command.flat_${haKey}_running
- alias: "Flat ${APPLIANCE_LABEL[appliance.name]} → done"
  trigger:
    - platform: numeric_state
      entity_id: <POWER_SENSOR>
      below: 5
      for: "00:02:00"
  action:
    - service: rest_command.flat_${haKey}_done`;

  const copyHaConfig = () => {
    navigator.clipboard.writeText(haConfig);
    setHaCopied(true);
    setTimeout(() => setHaCopied(false), 2000);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/appliance/${appliance.id}`, {
        nextApplianceId: nextApplianceId || null,
        haManaged,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${APPLIANCE_LABEL[appliance.name]}?`)) return;
    try {
      await api.delete(`/appliance/${appliance.id}`);
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Couldn't delete."));
    }
  };

  const next = appliances.find((x) => x.id === appliance.nextApplianceId);

  if (editing) {
    return (
      <li className="px-3 py-3">
        <form onSubmit={save} className="space-y-2">
          <div className="text-sm font-medium text-slate-900">{APPLIANCE_LABEL[appliance.name]}</div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={haManaged}
              onChange={(e) => setHaManaged(e.target.checked)}
              disabled={saving}
              className="mt-0.5 h-4 w-4 rounded border-slate-300"
            />
            <span>
              HA-managed
              <span className="text-slate-400">
                {" "}— Home Assistant drives running/done via the power sensor; an NFC tap only
                claims the load (and marks unload when done).
              </span>
            </span>
          </label>
          <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
            <div>
              <Label>NFC URL</Label>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-600">
                <span className="min-w-0 flex-1 truncate">{nfcUrl}</span>
                <button type="button" onClick={copyNfcUrl} className="shrink-0 text-brand-600 hover:text-brand-700 font-sans font-medium">
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor={`next-${appliance.id}`}>Next in chain</Label>
              <Select
                id={`next-${appliance.id}`}
                value={nextApplianceId}
                onChange={(e) => setNextApplianceId(e.target.value)}
                disabled={saving}
              >
                <option value="">None</option>
                {appliances.filter((x) => x.id !== appliance.id).map((x) => (
                  <option key={x.id} value={x.id}>{APPLIANCE_LABEL[x.name]}</option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <Label>Home Assistant</Label>
            <pre className="max-h-56 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
              {haConfig}
            </pre>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" onClick={copyHaConfig}>
                {haCopied ? "Copied!" : "Copy HA config"}
              </Button>
              <span className="text-xs text-slate-400">
                Set <code>&lt;POWER_SENSOR&gt;</code> to your plug's power entity; store the
                webhook secret in HA <code>secrets.yaml</code> as <code>flat_webhook_secret</code>.
              </span>
            </div>
          </div>
          <ErrorBanner message={error} />
          <div className="flex gap-2">
            <Button type="submit" loading={saving}>Save</Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900">{APPLIANCE_LABEL[appliance.name]}</div>
        <div className="truncate text-xs text-slate-500">
          <button type="button" onClick={copyNfcUrl} className="font-medium text-brand-600 hover:text-brand-700">
            {copied ? "Copied!" : "Copy NFC URL"}
          </button>
          {next && <span> · Chains to {APPLIANCE_LABEL[next.name]}</span>}
        </div>
      </div>
      <Pill tone="slate">{appliance.state}</Pill>
      <IconButton icon={<LuPencil className="h-4 w-4" />} label="Edit appliance" onClick={() => setEditing(true)} />
      <IconButton icon={<LuTrash2 className="h-4 w-4" />} label="Delete appliance" onClick={remove} variant="danger" />
    </li>
  );
}

function ApplianceSection({ flatId }: { flatId: string }) {
  const [appliances, setAppliances] = useState<Appliance[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState<ApplianceName>("WASHER");
  const [nextApplianceId, setNextApplianceId] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<Appliance[]>(`/appliance/by-flat/${flatId}`);
      setAppliances(data);
    } catch (err) {
      setError(errMsg(err, "Couldn't load appliances."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    try {
      await api.post<Appliance>(`/appliance/by-flat/${flatId}`, {
        name,
        nextApplianceId: nextApplianceId || undefined,
      });
      setNextApplianceId("");
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't add appliance."));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card>
      <SectionHeader title="Appliances" subtitle="Manage washer, dryer and dishwasher." />

      {appliances === null ? (
        <CenteredSpinner />
      ) : appliances.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
          No appliances configured yet.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
          {appliances.map((a) => (
            <ApplianceRow
              key={a.id}
              appliance={a}
              appliances={appliances}
              onSaved={load}
            />
          ))}
        </ul>
      )}

      <form onSubmit={add} className="mt-5 grid gap-3 sm:grid-cols-[140px_1fr_auto]">
        <div>
          <Label htmlFor="aname">Type</Label>
          <Select id="aname" value={name} onChange={(e) => setName(e.target.value as ApplianceName)} disabled={adding}>
            {APPLIANCE_OPTIONS.map((n) => (
              <option key={n} value={n}>{APPLIANCE_LABEL[n]}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="anext">Next in chain</Label>
          <Select id="anext" value={nextApplianceId} onChange={(e) => setNextApplianceId(e.target.value)} disabled={adding}>
            <option value="">None</option>
            {(appliances ?? []).map((a) => (
              <option key={a.id} value={a.id}>{APPLIANCE_LABEL[a.name]}</option>
            ))}
          </Select>
        </div>
        <div className="flex items-end">
          <Button type="submit" loading={adding} className="w-full">
            Add
          </Button>
        </div>
      </form>

      <ErrorBanner message={error} />
    </Card>
  );
}
